import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { useCoachChat } from './CoachChatContext'
import { useTranslation } from '~/hooks/useTranslation'
import { Typography } from '../../_atoms'
import AttachmentDisplay from '../candidate/Chat/AttachmentDisplay'

/**
 * CoachTextMessages component - displays chat messages in a scrollable container
 * Based on the candidate text messages but adapted for coach use
 *
 * @param {Object} props
 * @param {Array} props.messages - Array of chat messages
 * @param {Object} props.selectedCandidate - The selected candidate for this chat slot
 */
const CoachTextMessages = ({ messages, selectedCandidate }) => {
  const scrollContainerRef = useRef(null)
  const isInitialLoadRef = useRef(true)
  const typingTimeoutRef = useRef(null)
  const [isCandidateTyping, setIsCandidateTyping] = useState(false)
  const { chatManager, getDarkModeClasses } = useCoachChat()
  const { t } = useTranslation('coach_chat')

  const scrollToBottom = React.useCallback((behavior = 'smooth') => {
    const container = scrollContainerRef.current
    if (!container) return
    container.scrollTo({ top: container.scrollHeight, behavior })
  }, [])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    // Skip auto-scroll on initial load
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false
      return
    }

    // Only auto-scroll if there are actual messages
    if (messages && messages.length > 0) {
      scrollToBottom('smooth')
    }
  }, [messages, scrollToBottom])

  // Listen for candidate typing indicators (filtered by selectedCandidate)
  useEffect(() => {
    if (!selectedCandidate) return

    const handleCandidateTyping = (data) => {
      // Only show typing indicator if it's from this chat's candidate
      const fromEasyrtcid = data?.fromEasyrtcid || data
      if (
        fromEasyrtcid &&
        selectedCandidate.easyrtcid &&
        fromEasyrtcid === selectedCandidate.easyrtcid
      ) {
        // Clear any existing timeout to prevent weird display when events trickle in
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current)
        }

        setIsCandidateTyping(true)

        // Clear typing indicator after 10 seconds
        typingTimeoutRef.current = setTimeout(() => {
          setIsCandidateTyping(false)
          typingTimeoutRef.current = null
        }, 10000)
      }
    }

    // Listen for messages to clear typing indicator immediately
    const handleMessageReceived = (data) => {
      // Only clear if message is from this chat's candidate
      const fromEasyrtcid = data?.fromEasyrtcid || data?.message?.senderId
      if (
        fromEasyrtcid &&
        selectedCandidate.easyrtcid &&
        fromEasyrtcid === selectedCandidate.easyrtcid
      ) {
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current)
          typingTimeoutRef.current = null
        }
        setIsCandidateTyping(false)
      }
    }

    if (chatManager) {
      chatManager.addEventListener('candidateTyping', handleCandidateTyping)
      chatManager.addEventListener('peerMessage', handleMessageReceived)
      return () => {
        // Clean up timeout on unmount
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current)
        }
        chatManager.removeEventListener(
          'candidateTyping',
          handleCandidateTyping,
        )
        chatManager.removeEventListener('peerMessage', handleMessageReceived)
      }
    }
  }, [chatManager, selectedCandidate])

  // Auto-scroll to bottom when candidate typing indicator appears
  useEffect(() => {
    if (isCandidateTyping) {
      scrollToBottom('smooth')
    }
  }, [isCandidateTyping, scrollToBottom])

  // Helper function to format message timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return ''
    return moment(timestamp).format('LT') // Local time format (e.g., "2:30 PM")
  }

  // Helper function to render message content safely
  const renderMessageContent = (content) => {
    if (!content) return ''

    // Check if this is a "sent {filename}" message and apply ellipsis to filename
    const sentFileMatch = content.match(/^(sent\s+)(.+)$/i)
    if (sentFileMatch) {
      const [, prefix, filename] = sentFileMatch
      // Escape HTML
      const escapedPrefix = prefix
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      const escapedFilename = filename
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      return (
        <span>
          {escapedPrefix}
          <span
            className="inline-block max-w-[200px] truncate align-bottom"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={filename}
          >
            {escapedFilename}
          </span>
        </span>
      )
    }

    // Escape HTML and convert line breaks to <br> tags
    const escapedContent = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br />')

    return <span dangerouslySetInnerHTML={{ __html: escapedContent }} />
  }

  // Helper function to get sender name
  const getSenderName = (message) => {
    if (message.senderName) {
      return message.senderName
    }
    if (message.who) {
      return message.who
    }
    if (message.sender) {
      return message.sender
    }
    return 'Unknown'
  }

  // Helper function to force download of file
  const handleDownload = async (downloadUrl, fileName) => {
    if (!downloadUrl) return

    try {
      // Fetch the file
      const response = await fetch(downloadUrl)
      if (!response.ok) {
        throw new Error('Failed to fetch file')
      }

      // Get the blob
      const blob = await response.blob()

      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob)

      // Create a temporary anchor element and trigger download
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName || 'download'
      document.body.appendChild(link)
      link.click()

      // Clean up
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Error downloading file:', error)
      // Fallback to opening in new tab if download fails
      window.open(downloadUrl, '_blank')
    }
  }

  // Helper function to render candidate_action messages
  const renderCandidateAction = (message) => {
    const actionType = message.actionType || message.actionData?.action_type
    const actionData = message.actionData || {}

    // Special handling for chat_attachment
    if (actionType === 'chat_attachment') {
      const fileName =
        actionData.attachment_name || message.attachment?.name || 'file'
      const downloadUrl = actionData.action_target || message.attachment?.url
      const senderName = getSenderName(message)

      return (
        <div className="message-action">
          <div className="text-base font-normal">
            <span>
              {senderName} {t('sent_file', 'sent')}{' '}
            </span>
            {downloadUrl ? (
              <button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleDownload(downloadUrl, fileName)
                }}
                className="inline-block max-w-full cursor-pointer truncate text-white underline"
                style={{
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'bottom',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  textAlign: 'left',
                }}
                title={fileName}
              >
                {fileName}
              </button>
            ) : (
              <span
                className="inline-block max-w-full truncate"
                style={{
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'bottom',
                }}
                title={fileName}
              >
                {fileName}
              </span>
            )}{' '}
            <span>
              {t('at', 'at')} {formatTimestamp(message.ts || message.timestamp)}
            </span>
          </div>
        </div>
      )
    }

    // Handle other action types
    let actionText = ''
    switch (actionType) {
      case 'navigate':
        actionText = `${t('navigated_to', 'navigated to')} ${actionData.action_target?.pathname || actionData.action_target || ''}`
        break
      case 'started_chat_from':
        actionText = `${t('started_chat_from', 'started chat from')} ${actionData.action_target || ''}`
        break
      case 'left_chat':
        actionText = t('candidate_has_left_chat', 'Candidate has left the chat')
        break
      case 'pause':
        actionText = actionData.paused
          ? t('paused', 'paused their camera')
          : t('unpaused', 'unpaused their camera')
        break
      case 'mute':
        actionText = actionData.paused
          ? t('muted', 'muted their audio')
          : t('unmuted', 'unmuted their audio')
        break
      case 'text_disabled':
      case 'text_enabled':
        // Not shown in chat - coach uses these to hide/show text panel only
        return null
      default:
        actionText = t('chat_attachment', 'uploaded a file')
    }

    const senderName = getSenderName(message)
    // For left_chat, show the full message without sender name
    if (actionType === 'left_chat') {
      return (
        <div className="message-action">
          <div className="text-base font-normal">
            {actionText} {t('at', 'at')}{' '}
            {formatTimestamp(message.ts || message.timestamp)}
          </div>
        </div>
      )
    }
    return (
      <div className="message-action">
        <div className="text-base font-normal">
          {senderName} {actionText} {t('at', 'at')}{' '}
          {formatTimestamp(message.ts || message.timestamp)}
        </div>
      </div>
    )
  }

  return (
    <div className="coach-text-messages relative flex h-full min-h-0 flex-col overflow-hidden">
      <div
        ref={scrollContainerRef}
        className="coach-text-messages-content min-h-0 flex-1 overflow-y-auto px-6 py-4"
      >
        {/* Render existing messages */}
        {messages &&
          messages.map((message, index) => {
            const senderName = getSenderName(message)
            // For coach side, "me" is the coach, "other" is the candidate
            const isOwnMessage =
              senderName.toLowerCase() === 'me' ||
              senderName.toLowerCase() === 'you' ||
              senderName.toLowerCase() === 'coach' ||
              message.sender === 'coach'

            // Check if message is a candidate_action
            const isCandidateAction = message.type === 'candidate_action'

            // Check if message has attachment (either direct attachment or from candidate_action)
            const hasAttachment = !!message.attachment

            // For candidate_action messages, render them differently (skip text_disabled/text_enabled)
            if (isCandidateAction) {
              const actionContent = renderCandidateAction(message)
              if (actionContent == null) return null
              return (
                <div
                  key={message.id || index}
                  className="mb-4 flex justify-start"
                >
                  <div className="max-w-[320px] rounded-r-xl rounded-t-xl bg-intoo-green-medium-800 px-4 py-3 text-white">
                    {actionContent}
                  </div>
                </div>
              )
            }

            return (
              <div
                key={message.id || index}
                className={`mb-4 flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[320px] px-4 py-3 ${
                    isOwnMessage
                      ? `rounded-l-xl rounded-t-xl ${getDarkModeClasses('bg-cod-gray-100 text-cod-gray-900', 'bg-gray-700 text-white')}`
                      : 'rounded-r-xl rounded-t-xl bg-intoo-green-medium-800 text-white'
                  }`}
                >
                  {/* Render attachment if present */}
                  {hasAttachment && (
                    <div className="mb-2">
                      <AttachmentDisplay
                        attachment={message.attachment}
                        isOwnMessage={isOwnMessage}
                        getDarkModeClasses={getDarkModeClasses}
                      />
                    </div>
                  )}

                  {/* Render message content if present */}
                  {(message.text || message.content) && (
                    <div
                      className={`text-base font-normal [&_a]:!cursor-pointer [&_a]:!underline ${
                        isOwnMessage
                          ? getDarkModeClasses(
                              '[&_a]:text-gray-900',
                              '[&_a]:text-white',
                            )
                          : '[&_a]:text-white'
                      }`}
                    >
                      {renderMessageContent(message.text || message.content)}
                    </div>
                  )}

                  <div
                    className={`mt-1 text-xs ${
                      isOwnMessage
                        ? // CTS-9777 / CC-UX-010: gray-500 on cod-gray-100 fails AA (~3.9:1);
                          // gray-600 on cod-gray-100 meets AA (~6.1:1)
                          getDarkModeClasses('text-gray-600', 'text-gray-300')
                        : 'text-intoo-green-medium-100'
                    }`}
                  >
                    {formatTimestamp(message.ts || message.timestamp)}
                  </div>
                </div>
              </div>
            )
          })}
      </div>

      {/* Show typing indicator when candidate is typing - absolute positioned bar at bottom */}
      {isCandidateTyping && (
        <div
          className={`absolute bottom-0 left-0 right-0 z-10 px-6 py-2 ${getDarkModeClasses(
            'bg-gray-100/80 backdrop-blur-sm',
            'bg-gray-800/80 backdrop-blur-sm',
          )}`}
        >
          <Typography
            variant="small"
            className={getDarkModeClasses('text-gray-700', 'text-gray-300')}
          >
            {selectedCandidate?.candidateName ||
              selectedCandidate?.firstName ||
              'Candidate'}{' '}
            is typing...
          </Typography>
        </div>
      )}
    </div>
  )
}

export default CoachTextMessages
